import { createSlice } from '@reduxjs/toolkit';

const chatRealtimeSlice = createSlice({
    name: 'chatRealtime',
    initialState: {
        loading: false,
        chat: null,
        conversations: [],
        activeConversationId: null,
        messages: [],
        participants: [],
        onlineUsers: [],
        typingUsers: [],
        unreadCounts: {},
        connected: false,
    },
    reducers: {
        setLoading: (state, { payload }) => {
            state.loading = payload;
        },
        setChatData: (state, { payload }) => {
            state.chat = payload.chat;
            state.conversations = payload.conversations || [];
            state.participants = payload.participants || [];
            state.onlineUsers = payload.onlineUsers || [];
            const firstConv = payload.conversations?.[0];
            state.activeConversationId = firstConv?._id?.toString?.() || firstConv?._id || null;
        },
        setActiveConversation: (state, { payload }) => {
            state.activeConversationId = payload;
        },
        setMessages: (state, { payload }) => {
            state.messages = payload;
        },
        prependMessages: (state, { payload }) => {
            const existingIds = new Set(state.messages.map((m) => String(m._id)));
            const incoming = payload.filter((m) => !existingIds.has(String(m._id)));
            state.messages = [...incoming, ...state.messages];
        },
        addMessage: (state, { payload }) => {
            const msgConvId = payload.conversationId?.toString?.() || payload.conversationId;
            const activeId = state.activeConversationId?.toString?.() || state.activeConversationId;
            if (activeId && msgConvId && msgConvId !== activeId) return;

            const senderId = payload.sender?._id?.toString?.() || payload.sender?._id;
            if (senderId && msgConvId) {
                state.typingUsers = state.typingUsers.filter(
                    (t) => !(
                        (t.user?._id?.toString?.() || t.user?._id) === senderId
                        && (t.conversationId?.toString?.() || t.conversationId) === msgConvId
                    ),
                );
            }

            // Si coincide con un mensaje optimista (mismo clientId), reemplazarlo (confirmar)
            if (payload.clientId) {
                const optimisticIndex = state.messages.findIndex((m) => m.clientId === payload.clientId);
                if (optimisticIndex !== -1) {
                    state.messages[optimisticIndex] = { ...payload, status: 'sent' };
                    return;
                }
            }

            const exists = state.messages.some((m) => m._id === payload._id);
            if (!exists) {
                state.messages.push({ ...payload, status: 'sent' });
            }
        },
        addOptimisticMessage: (state, { payload }) => {
            const msgConvId = payload.conversationId?.toString?.() || payload.conversationId;
            const senderId = payload.sender?._id?.toString?.() || payload.sender?._id;
            if (senderId && msgConvId) {
                state.typingUsers = state.typingUsers.filter(
                    (t) => !(
                        (t.user?._id?.toString?.() || t.user?._id) === senderId
                        && (t.conversationId?.toString?.() || t.conversationId) === msgConvId
                    ),
                );
            }
            state.messages.push({ ...payload, status: 'sending' });
        },
        confirmMessage: (state, { payload: { clientId, message } }) => {
            const index = state.messages.findIndex((m) => m.clientId === clientId);
            if (index !== -1) {
                state.messages[index] = { ...message, status: 'sent' };
            } else {
                const exists = state.messages.some((m) => m._id === message._id);
                if (!exists) {
                    state.messages.push({ ...message, status: 'sent' });
                }
            }
        },
        failMessage: (state, { payload: { clientId } }) => {
            const index = state.messages.findIndex((m) => m.clientId === clientId);
            if (index !== -1) {
                state.messages[index].status = 'failed';
            }
        },
        retryMessageStart: (state, { payload: { clientId } }) => {
            const index = state.messages.findIndex((m) => m.clientId === clientId);
            if (index !== -1) {
                state.messages[index].status = 'sending';
            }
        },
        removeMessage: (state, { payload: { clientId } }) => {
            state.messages = state.messages.filter((m) => m.clientId !== clientId);
        },
        setOnlineUsers: (state, { payload }) => {
            state.onlineUsers = payload;
            const onlineIds = new Set(payload.map((u) => u._id?.toString?.() || u._id));
            state.participants = state.participants.map((p) => ({
                ...p,
                isOnline: onlineIds.has(p._id?.toString?.() || p._id),
            }));
        },
        addOnlineUser: (state, { payload }) => {
            const userId = payload._id?.toString?.() || payload._id;
            const exists = state.onlineUsers.some((u) => u._id === userId || u._id?.toString?.() === userId);
            if (!exists) {
                state.onlineUsers.push(payload);
            }
            state.participants = state.participants.map((p) => (
                (p._id === userId || p._id?.toString?.() === userId)
                    ? { ...p, isOnline: true }
                    : p
            ));
        },
        removeOnlineUser: (state, { payload }) => {
            const userId = payload?.toString?.() || payload;
            state.onlineUsers = state.onlineUsers.filter(
                (u) => u._id !== userId && u._id?.toString?.() !== userId,
            );
            state.participants = state.participants.map((p) => (
                (p._id === userId || p._id?.toString?.() === userId)
                    ? { ...p, isOnline: false }
                    : p
            ));
        },
        setTypingUsers: (state, { payload }) => {
            state.typingUsers = payload;
        },
        addTypingUser: (state, { payload }) => {
            const userId = payload.user?._id?.toString?.() || payload.user?._id;
            const exists = state.typingUsers.some(
                (u) => (u.user?._id?.toString?.() || u.user?._id) === userId
                    && u.conversationId === payload.conversationId,
            );
            if (!exists) {
                state.typingUsers.push(payload);
            }
        },
        removeTypingUser: (state, { payload }) => {
            const userId = payload.userId?.toString?.() || payload.userId;
            state.typingUsers = state.typingUsers.filter(
                (t) => !((t.user?._id?.toString?.() || t.user?._id) === userId
                    && t.conversationId === payload.conversationId),
            );
        },
        setUnreadCounts: (state, { payload }) => {
            state.unreadCounts = payload.reduce((acc, item) => {
                acc[item.conversationId] = item.unreadCount;
                return acc;
            }, {});
        },
        updateUnreadCount: (state, { payload }) => {
            state.unreadCounts[payload.conversationId] = payload.unreadCount;
        },
        setConnected: (state, { payload }) => {
            state.connected = payload;
        },
        resetChatRealtime: (state) => {
            state.chat = null;
            state.conversations = [];
            state.activeConversationId = null;
            state.messages = [];
            state.participants = [];
            state.onlineUsers = [];
            state.typingUsers = [];
            state.connected = false;
        },
    },
});

export const {
    setLoading,
    setChatData,
    setActiveConversation,
    setMessages,
    prependMessages,
    addMessage,
    addOptimisticMessage,
    confirmMessage,
    failMessage,
    retryMessageStart,
    removeMessage,
    setOnlineUsers,
    addOnlineUser,
    removeOnlineUser,
    setTypingUsers,
    addTypingUser,
    removeTypingUser,
    setUnreadCounts,
    updateUnreadCount,
    setConnected,
    resetChatRealtime,
} = chatRealtimeSlice.actions;

export default chatRealtimeSlice.reducer;
